/**
 * Connector foundation — provider-neutral core.
 *
 * Commerce OS Product/Variant data is canonical internal business data. External
 * marketplace/store identifiers live in connector/channel mapping records
 * (external_identifiers / channel_listings), never in the Product/Variant primary
 * identity. This module holds the pure, testable connector contract that mirrors
 * the database rules and the edge-function adapters so UI, tests and backend
 * cannot drift.
 *
 * It contains NO provider-specific API calls and NO secrets — adapters are
 * injected and credentials are referenced by name, never held in the browser.
 */

import { assertAmazonProviderWriteAllowed } from "@/lib/providers/amazon-write-guard";

// ---------------------------------------------------------------------------
// Canonical connection status (single authoritative model)
// ---------------------------------------------------------------------------

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "degraded"
  | "disabled";

export const CONNECTION_STATUSES: ConnectionStatus[] = [
  "disconnected",
  "connecting",
  "connected",
  "degraded",
  "disabled",
];

/**
 * Mirrors database `derive_connection_status(enabled, auth_status, health_status)`
 * (see migrations 0011 + 0253). `connection_status` is always derived by trigger,
 * never written as an independent lifecycle field.
 *
 * Authenticated auth values: BigCommerce uses `authorized`, eBay uses `connected`.
 * Automatic Sync OFF (`enabled=false`) yields `disabled` (not auth disconnect).
 */
export function deriveConnectionStatus(
  enabled: boolean,
  authStatus: string | null | undefined,
  healthStatus: string | null | undefined,
): ConnectionStatus {
  if (enabled !== true) return "disabled";
  const a = (authStatus ?? "").trim();
  if (a === "" || a === "not_configured") return "disconnected";
  if (a === "connecting" || a === "pending" || a === "configured") return "connecting";
  if (a === "authorized" || a === "connected") {
    return healthStatus === "error" ? "degraded" : "connected";
  }
  if (a === "expired" || a === "revoked" || a === "error" || a === "degraded") return "degraded";
  return "disconnected";
}

export interface ConnectionStatusMeta {
  status: ConnectionStatus;
  label: string;
  /** Variant name accepted by StatusBadge. */
  badge: string;
  icon: string;
  tone: "positive" | "neutral" | "warning" | "danger" | "info";
}

export function connectionStatusMeta(status: ConnectionStatus): ConnectionStatusMeta {
  switch (status) {
    case "connected":
      return { status, label: "Connected", badge: "success", icon: "ri-check-double-line", tone: "positive" };
    case "connecting":
      return { status, label: "Connecting", badge: "pending", icon: "ri-loader-4-line", tone: "info" };
    case "disconnected":
      return { status, label: "Disconnected", badge: "inactive", icon: "ri-plug-line", tone: "neutral" };
    case "degraded":
      return { status, label: "Degraded", badge: "warning", icon: "ri-alert-line", tone: "warning" };
    case "disabled":
      return { status, label: "Disabled", badge: "inactive", icon: "ri-toggle-line", tone: "neutral" };
  }
}

// ---------------------------------------------------------------------------
// Error classification / retry / backoff (mirrors the sync worker)
// ---------------------------------------------------------------------------

export type ErrorClass = "retryable" | "permanent" | "auth" | "rate_limited" | "not_configured";

export function classifyConnectorError(status: number): ErrorClass {
  if (status === 401) return "auth";
  if (status === 403) return "permanent";
  if (status === 429) return "rate_limited";
  if (status >= 500) return "retryable";
  if (status >= 400) return "permanent";
  return "retryable";
}

export function shouldRetry(errorClass: ErrorClass, attempt: number, maxAttempts: number): boolean {
  if (attempt >= maxAttempts) return false;
  return errorClass === "retryable" || errorClass === "rate_limited";
}

export function backoffDelayMs(attempt: number, errorClass: ErrorClass): number {
  const base = errorClass === "rate_limited" ? 300_000 : 60_000 * Math.pow(2, attempt);
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(base + jitter));
}

// ---------------------------------------------------------------------------
// Idempotency / deduplication (pure helpers)
// ---------------------------------------------------------------------------

/** An already-imported external order id must not be imported again. */
export function isDuplicateOrder(importedExternalIds: ReadonlySet<string>, externalOrderId: string): boolean {
  return importedExternalIds.has(externalOrderId);
}

/** A repeated webhook delivery (same connector + event id) must be a no-op. */
export function isDuplicateWebhookEvent(seenEventIds: ReadonlySet<string>, eventId: string): boolean {
  return seenEventIds.has(eventId);
}

// ---------------------------------------------------------------------------
// Channel mapping (connector-scoped, mirrors external_identifiers)
// ---------------------------------------------------------------------------

export interface ExternalMapping {
  connectorId: string;
  entityType: "product" | "variant" | "sales_order" | "customer";
  externalId: string;
  entityId: string;
}

/**
 * Resolve an external id to a canonical entity within a single connector scope.
 * Store A's mapping never resolves against Store B — connector isolation is
 * enforced by filtering on `connectorId` here and by a connector-scoped unique
 * index in the database.
 */
export function resolveExternalEntity(
  mappings: ExternalMapping[],
  connectorId: string,
  entityType: ExternalMapping["entityType"],
  externalId: string,
): string | null {
  const hit = mappings.find(
    (m) => m.connectorId === connectorId && m.entityType === entityType && m.externalId === externalId,
  );
  return hit?.entityId ?? null;
}

// ---------------------------------------------------------------------------
// Capabilities (single source of truth, mirrors register_connector + registry)
// ---------------------------------------------------------------------------

/**
 * Canonical provider capability vocabulary.
 *
 * This is the single source of truth for what a provider can do. Future
 * providers (Amazon, Shopify, Meta, Temu, AliExpress, …) are added by declaring
 * their capabilities here — NOT by forking core commerce or adding
 * provider-specific branches to the Product/Variant master.
 */
export const PROVIDER_CAPABILITIES: Record<string, Record<string, boolean>> = {
  bigcommerce: {
    import_products: true,
    import_orders: true,
    publish_inventory: true,
    publish_fulfillment: true,
    webhooks: true,
    auth_oauth: true,
    variants: true,
    variant_images: true,
    multi_location_inventory: true,
    partial_fulfilment: true,
    category_discovery: true,
    listing_pause: true,
    returns: false,
    identifier_requirements: true,
    variant_limits: true,
  },
  ebay: {
    import_products: true,
    import_orders: true,
    publish_inventory: true,
    publish_fulfillment: true,
    webhooks: true,
    auth_oauth: true,
    notifications: true,
    variants: true,
    variant_images: true,
    multi_location_inventory: true,
    partial_fulfilment: false,
    category_discovery: true,
    listing_pause: true,
    returns: true,
    identifier_requirements: true,
    variant_limits: true,
  },
  shopify: {
    import_products: true,
    import_orders: true,
    publish_inventory: true,
    publish_fulfillment: true,
    webhooks: true,
    auth_oauth: true,
    variants: true,
    variant_images: true,
    multi_location_inventory: true,
    partial_fulfilment: true,
    category_discovery: true,
    listing_pause: true,
    returns: false,
    identifier_requirements: false,
    variant_limits: true,
  },
  amazon: {
    import_orders: false,
    import_products: false,
    publish_inventory: false,
    publish_fulfillment: false,
    auth_oauth: true,
    variants: true,
    variant_images: false,
    multi_location_inventory: true,
    partial_fulfilment: true,
    category_discovery: true,
    listing_pause: true,
    returns: true,
    identifier_requirements: false,
    variant_limits: true,
  },
  woocommerce: {
    import_orders: true,
    variants: true,
    variant_images: true,
  },
};

export function capabilitiesFor(provider: string | null | undefined): Record<string, boolean> {
  if (!provider) return {};
  // Unknown/future providers default to order import only until they register
  // their capabilities — never an implicit "full" capability set.
  return PROVIDER_CAPABILITIES[provider] ?? { import_orders: true };
}

export function supports(provider: string | null | undefined, capability: string): boolean {
  return capabilitiesFor(provider)[capability] === true;
}

/**
 * Minimal shape of a connector record's capability source (either the
 * `capabilities` or legacy `provider_capabilities` JSONB column).
 */
export interface ConnectorCapabilitySource {
  provider?: string | null;
  capabilities?: Record<string, unknown> | null;
  provider_capabilities?: Record<string, unknown> | null;
}

/**
 * Resolve the effective capability map for a specific connector.
 *
 * Prefers the per-connector capabilities stored in the database (which may be
 * narrowed by the operator or enriched at registration), falling back to the
 * static provider defaults. This lets a future provider self-describe without
 * any change to core commerce — the capability map is the contract.
 */
export function connectorCapabilities(
  connector: ConnectorCapabilitySource | null | undefined,
): Record<string, boolean> {
  const provider = connector?.provider;
  const defaults = capabilitiesFor(provider);
  const stored = connector?.capabilities ?? connector?.provider_capabilities;

  if (!stored || typeof stored !== "object") return defaults;

  const merged: Record<string, boolean> = { ...defaults };
  for (const [key, value] of Object.entries(stored)) {
    if (typeof value === "boolean") merged[key] = value;
  }
  return merged;
}

export function connectorSupports(
  connector: ConnectorCapabilitySource | null | undefined,
  capability: string,
): boolean {
  return connectorCapabilities(connector)[capability] === true;
}

/** Only publish non-negative, whole-unit available inventory. */
export function clampAvailableQty(qty: number): number {
  return Math.max(0, Math.floor(qty));
}

// ---------------------------------------------------------------------------
// Generic connector adapter + orchestrator (injected adapter, no providers)
// ---------------------------------------------------------------------------

export interface AdapterResult {
  ok: boolean;
  result?: Record<string, unknown>;
  error?: string;
  errorClass?: ErrorClass;
}

export interface ConnectorAdapter {
  provider: string;
  capabilities: Record<string, boolean>;
  /** Load credentials from server-side secrets by reference — never from the client. */
  loadCredential(ref: string | null): Record<string, string> | null;
  testConnection(creds: Record<string, string>): Promise<AdapterResult>;
  publishInventory(creds: Record<string, string>, ctx: { availableQty: number; externalId: string | null }): Promise<AdapterResult>;
  importOrder(creds: Record<string, string>, ctx: { externalOrderId: string }): Promise<AdapterResult>;
}

export interface ConnectorState {
  orgId: string;
  enabled: boolean;
  authStatus: string;
  healthStatus: string;
  credentialRef: string | null;
}

export type SyncJobType = "test_connection" | "inventory_publish" | "order_import";

export interface SyncJob {
  orgId: string;
  connector: ConnectorState;
  jobType: SyncJobType;
  attempts: number;
  maxAttempts: number;
  payload: Record<string, unknown>;
}

export type ProcessOutcomeStatus = "succeeded" | "retrying" | "failed";

export interface ProcessOutcome {
  status: ProcessOutcomeStatus;
  errorClass?: ErrorClass;
  error?: string;
  result?: Record<string, unknown>;
  tenantIsolated?: boolean;
}

/**
 * Provider-neutral job orchestrator. Mirrors `marketplace-sync` edge worker:
 *  1. tenant isolation — never process another org's connector,
 *  2. disabled connectors never process,
 *  3. credentials are referenced (never in the browser) and must resolve,
 *  4. dispatch by job type to the injected adapter,
 *  5. success is only ever recorded on an actual adapter success,
 *  6. failures are classified terminal vs retryable, respecting max attempts.
 * No provider-specific branching lives here — the adapter encapsulates it.
 */
export async function processConnectorJob(
  tenantOrgId: string,
  job: SyncJob,
  adapter: ConnectorAdapter,
): Promise<ProcessOutcome> {
  const amazonWrite = assertAmazonProviderWriteAllowed(adapter.provider);
  if (amazonWrite.allowed === false) {
    return { status: "failed", errorClass: "permanent", error: amazonWrite.error };
  }
  if (job.connector.orgId !== tenantOrgId || job.orgId !== tenantOrgId) {
    return { status: "failed", errorClass: "permanent", error: "Tenant isolation violation", tenantIsolated: true };
  }

  const conn = job.connector;

  if (!conn.enabled) {
    return { status: "failed", errorClass: "permanent", error: "Connector is disabled" };
  }

  if (!conn.credentialRef) {
    return { status: "failed", errorClass: "not_configured", error: "No server-side credentials configured" };
  }
  const creds = adapter.loadCredential(conn.credentialRef);
  if (!creds) {
    return { status: "failed", errorClass: "not_configured", error: "Credentials not configured server-side" };
  }

  let result: AdapterResult;
  if (job.jobType === "test_connection") {
    result = await adapter.testConnection(creds);
  } else if (job.jobType === "inventory_publish") {
    const availableQty = clampAvailableQty(Number(job.payload.availableQty ?? 0));
    const externalId = (job.payload.externalId as string | null) ?? null;
    result = await adapter.publishInventory(creds, { availableQty, externalId });
  } else if (job.jobType === "order_import") {
    result = await adapter.importOrder(creds, { externalOrderId: String(job.payload.externalOrderId ?? "") });
  } else {
    result = { ok: false, error: `Unknown job type ${job.jobType}`, errorClass: "permanent" };
  }

  const attempts = job.attempts + 1;

  if (result.ok) {
    return { status: "succeeded", result: result.result ?? {} };
  }

  const errorClass = result.errorClass ?? "retryable";
  const terminal = errorClass === "not_configured" || errorClass === "permanent" || errorClass === "auth";
  if (terminal || !shouldRetry(errorClass, attempts, job.maxAttempts)) {
    return { status: "failed", errorClass, error: result.error };
  }
  return { status: "retrying", errorClass, error: result.error };
}